import { Button as ButtonPrimitive } from '@base-ui/react/button'

import { cn } from '@/shared/lib/utils'

type SwitchSize = 'default' | 'sm'

interface SwitchProps extends Omit<ButtonPrimitive.Props, 'onClick'> {
  checked: boolean
  onCheckedChange?: (checked: boolean) => void
  size?: SwitchSize
  decorative?: boolean
  thumbClassName?: string
}

const trackSizeClasses: Record<SwitchSize, string> = {
  default: 'h-5 w-9',
  sm: 'h-4 w-7',
}

const thumbSizeClasses: Record<SwitchSize, string> = {
  default: 'left-[2px] h-4 w-4',
  sm: 'left-[2px] h-3 w-3',
}

const thumbPositionClasses: Record<SwitchSize, { on: string; off: string }> = {
  default: {
    on: 'translate-x-[16px]',
    off: 'translate-x-0',
  },
  sm: {
    on: 'translate-x-[12px]',
    off: 'translate-x-0',
  },
}

function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  size = 'default',
  decorative = false,
  className,
  thumbClassName,
  ...props
}: SwitchProps) {
  const trackClassName = cn(
    'relative inline-flex shrink-0 rounded-full border-[0.5px] p-0 transition-colors duration-150',
    trackSizeClasses[size],
    checked
      ? 'border-[var(--green)] bg-[var(--green)]'
      : 'border-input bg-[var(--bg-subtle)]',
    !decorative && 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--green-border)] focus-visible:ring-offset-0',
    !decorative && !disabled && checked && 'hover:bg-[var(--green-hover)] hover:border-[var(--green-hover)]',
    !decorative && !disabled && !checked && 'hover:bg-[var(--bg)] hover:border-[var(--rule-strong)]',
    disabled && 'cursor-not-allowed opacity-50',
    className
  )

  const knobClassName = cn(
    'absolute top-1/2 -translate-y-1/2 rounded-full bg-[var(--bg)] shadow-sm transition-transform duration-150',
    thumbSizeClasses[size],
    checked ? thumbPositionClasses[size].on : thumbPositionClasses[size].off,
    thumbClassName
  )

  if (decorative) {
    return (
      <span aria-hidden="true" className={trackClassName}>
        <span className={knobClassName} />
      </span>
    )
  }

  return (
    <ButtonPrimitive
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={trackClassName}
      onClick={() => onCheckedChange?.(!checked)}
      {...props}
    >
      <span aria-hidden="true" className={knobClassName} />
    </ButtonPrimitive>
  )
}

export { Switch }
